import React, { useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import { PageContainer, PageOrgSkeleton, PermissionLabel, Poppins, Spacer, StyledAvatar } from 'src/common';
import styled, { css } from 'styled-components';
import { useParams } from 'react-router-dom';
import { getProfileCredentials } from 'src/utils/auth';
import { useAuth } from 'src/state/auth';
import TabsMenu from 'src/components/TabsMenu';
import { Details, Members } from './tabs';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useOrganisationQuery } from 'src/graphql/_generated';
import { getRoleLabel } from 'src/api/types';
import { useImageLightness } from 'src/hooks/useImageLightness';

const Div = styled(PageContainer)`
  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__left {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 17px;
      grid-gap: 17px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .head {
      flex-direction: column;
      align-items: flex-start;
      grid-gap: 10px;
    }
  }
`;

const LogoAvatar = styled(StyledAvatar)<{ $shadow?: boolean }>`
  img {
    ${({ $shadow }) =>
      $shadow &&
      css`
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3)) drop-shadow(0 0 1px rgba(0, 0, 0, 0.25));
      `}
  }
`;

const OrganisationSettingsPage = () => {
  const { id } = useParams<{ id?: string }>();
  const { isPlatformAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(1);
  const isSm = useBreakpoints('sm');
  useGaEventPageView('Cydea | Organisation | Settings');

  const { data: organisationData, error, loading } = useOrganisationQuery({ variables: { id } });
  const logoIsLight = useImageLightness(organisationData?.organisation?.logoUrl);
  const shouldShadowLogo = Boolean(organisationData?.organisation?.logoUrl) && (logoIsLight ?? true);

  return (
    <>
      <Meta title={`Cydea | Organisation | Settings`} feedbackScript />
      <>
        <Div data-cy="org-settings-page">
          <Poppins px={32} weight={600}>
            Organisation Settings
          </Poppins>
          <Spacer $px={isSm ? 30 : 65} />
          {!!error && (
            <Poppins px={18} color={'error'}>
              {/* @ts-ignore */}
              {error.status} {error.msg || error.message}
            </Poppins>
          )}
          {loading ? (
            <PageOrgSkeleton />
          ) : (
            <>
              {organisationData?.organisation && (
                <>
                  <div className="head">
                    <div className="head__left">
                      <LogoAvatar $medium $shadow={shouldShadowLogo} src={organisationData.organisation.logoUrl || ''}>
                        {getProfileCredentials(organisationData.organisation.name)}
                      </LogoAvatar>
                      <Poppins px={22} weight={600}>
                        {organisationData.organisation.name}
                      </Poppins>
                    </div>
                    <PermissionLabel label={getRoleLabel(organisationData.organisation.member?.effectiveRole)} />
                  </div>
                  <Spacer $px={30} />
                  <TabsMenu
                    activeTab={activeTab}
                    setActiveTab={(tab) => {
                      setActiveTab(tab);
                      mpEvent(MPEvents.ButtonClick, {
                        button:
                          tab === 1 ? (isPlatformAdmin ? 'Tab:Details' : 'Tab:Organisation Details') : 'Tab:Members',
                        tags: ['ORGANISATION'],
                      });
                    }}
                    tabs={[
                      { label: isPlatformAdmin ? 'Details' : 'Organisation Details', value: 1 },
                      { label: 'Members', value: 2 },
                    ]}
                  />
                  {activeTab === 1 && <Details organisationId={organisationData.organisation.id} />}
                  {activeTab === 2 && <Members organisationId={organisationData.organisation.id} />}
                </>
              )}
            </>
          )}
        </Div>
      </>
    </>
  );
};

export default OrganisationSettingsPage;
